import { Ref, useEffect, useEffectEvent, useState } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { ItemNoAndTypeWithOrderedDetails } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import {
  isLoading,
  Loading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import { useAllAttachedItems } from "@coworker/apprestructured/src/task-management/services/useAllAttachedItems";
import LoadingCreatorFragment from "../InvalidCreatorFragment/InvalidCreatorFragment";
import { ListItem } from "../../../SelectListItem/SelectListItem";
import { ArticlesQuantity } from "../../../ArticlesQuantity/ArticlesQuantity";

interface VisibilityCondition {
  field: string;
  value: any;
  operator: "in";
}

export interface ArticlesQuantityCreatorFragmentConfigProps {
  title: string;
  field: string;
  visibility: VisibilityCondition;
}

interface ArticlesQuantityCreatorFragmentProps {
  configProps: ArticlesQuantityCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
}

function shouldShowField(
  task: CreatorFragmentCommonProps["task"],
  visibility: VisibilityCondition
): boolean {
  const currentValue = getByPath(task, visibility.field) as ListItem | null;

  if (visibility.operator === "in") {
    return Array.isArray(visibility.value)
      ? visibility.value.includes(currentValue?.value)
      : false;
  }

  return true;
}

function normalizeOrderedDetails(
  value: ItemNoAndTypeWithOrderedDetails[] | null | Loading
): ItemNoAndTypeWithOrderedDetails[] | null | Loading {
  if (!Array.isArray(value)) {
    return value;
  }

  return value.map((item) => ({
    ...item,
    pieces_ordered: item.pieces_ordered ?? item.pieces,
    ptags_ordered: item.ptags_ordered ?? item.pieces,
  }));
}

const ArticlesQuantityCreatorFragment = ({
  configProps,
  commonProps,
}: ArticlesQuantityCreatorFragmentProps) => {
  const { field, visibility } = configProps;
  const { task, taskType, onTaskChange } = commonProps;

  const itemNosAndTypeWithOrderedDetailsFromTask =
    validateInitialDetailsFieldValue<ItemNoAndTypeWithOrderedDetails[]>(
      getByPath(task, field),
      field,
      taskType
    );

  const normalizedItemsOrderedDetails = normalizeOrderedDetails(
    itemNosAndTypeWithOrderedDetailsFromTask
  );

  const initializeItemsOrderedDetails = useEffectEvent(() => {
    if (
      !Array.isArray(itemNosAndTypeWithOrderedDetailsFromTask) ||
      isLoading(itemNosAndTypeWithOrderedDetailsFromTask)
    ) {
      return;
    }

    const hasOrderedFields = itemNosAndTypeWithOrderedDetailsFromTask.some(
      (item) => "pieces_ordered" in item || "ptags_ordered" in item
    );

    if (!hasOrderedFields) {
      const itemsWithOrderedDetails =
        itemNosAndTypeWithOrderedDetailsFromTask.map((item) => ({
          ...item,
          pieces_ordered: item.pieces,
          ptags_ordered: item.pieces,
        }));

      onTaskChange({ details: { items: itemsWithOrderedDetails } });
    }
  });

  useEffect(() => {
    initializeItemsOrderedDetails();
  }, []);

  const [piecesQuantityOverrides, setPiecesQuantityOverrides] = useState<
    Record<string, number>
  >({});

  const itemsOrderedDetails = Array.isArray(normalizedItemsOrderedDetails)
    ? normalizedItemsOrderedDetails.map((item) => ({
        ...item,
        pieces_ordered: piecesQuantityOverrides[item.no] ?? item.pieces_ordered,
      }))
    : normalizedItemsOrderedDetails;

  const handlePiecesChange = (itemNo: string, newPieces: number) => {
    setPiecesQuantityOverrides((prev) => ({
      ...prev,
      [itemNo]: newPieces,
    }));
  };

  const handleConfirm = () => {
    if (Array.isArray(itemsOrderedDetails)) {
      onTaskChange({ details: { items: itemsOrderedDetails } });
    }
  };

  const { items, isFetchingItems } = useAllAttachedItems(
    itemNosAndTypeWithOrderedDetailsFromTask ?? LOADING
  );

  if (!shouldShowField(task, visibility)) {
    return null;
  }

  if (isFetchingItems || isLoading(itemNosAndTypeWithOrderedDetailsFromTask)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <ArticlesQuantity
      itemsNoAndTypeOrderedDetails={itemsOrderedDetails}
      items={items}
      handlePiecesChange={handlePiecesChange}
      handleConfirm={handleConfirm}
    />
  );
};

export default ArticlesQuantityCreatorFragment;
